import { auth } from "../firebase";
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import Layout from "../components/Layout";
import Papa from "papaparse";
import { exportToExcel } from "../utils/exportToExcel";

function DistributorManager() {
  const [activeTab, setActiveTab] = useState("edit"); // create | edit | bulk
  const [parties, setParties] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [userProfile, setUserProfile] = useState(null);

  // ---------- LOGIN CREATION STATE ----------
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedPartyForLogin, setSelectedPartyForLogin] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [isCreatingLogin, setIsCreatingLogin] = useState(false);

  // ---------- CREATE DISTRIBUTOR FORM ----------
  const [newDistributor, setNewDistributor] = useState({
    partyId: "",
    name: "",
    address: "",
    route: "",
    distance: "",
    gstno: "",
    phone: "",
    clientId: "",
    vehicleno: "",
    dms_name: "",
    easycig_name: "",
    partytype: "distributor",
    creditLimit: "",
    opening_balance: "",
  });

  // ---------- BULK UPLOAD STATE ----------
  const [csvPreview, setCsvPreview] = useState([]);

  // Load parties once
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        const profile = snap.data();
        setUserProfile(profile);
        loadParties(profile);
      }
    });
    return () => unsub();
  }, []);

  const loadParties = async (profile) => {
    let q = collection(db, "partyMaster");
    if (profile && profile.role !== "superadmin") {
      q = query(collection(db, "partyMaster"), where("clientId", "==", profile.clientId));
    }
    const partySnap = await getDocs(q);
    const list = partySnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setParties(list);
    setFiltered(list);
  };


  const handleCreateLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      alert("Email and Password are required.");
      return;
    }
    setIsCreatingLogin(true);
    try {
      const functions = getFunctions();
      const createDistributorUser = httpsCallable(functions, "createDistributorUser");
      await createDistributorUser({
        email: loginForm.email.trim(),
        password: loginForm.password,
        partyId: selectedPartyForLogin.partyId,
        name: selectedPartyForLogin.name,
        clientId: userProfile?.clientId || "mdsons"
      });
      alert("Login created successfully!");
      setShowLoginModal(false);
      setLoginForm({ email: "", password: "" });
      loadParties();
    } catch (err) {
      console.error(err);
      alert(`Error: ${err.message || "Could not create login"}`);
    } finally {
      setIsCreatingLogin(false);
    }
  };

  const onChangeNewDistributor = (field, value) => {
    setNewDistributor((prev) => ({ ...prev, [field]: value }));
  };

  // ============= TAB: CREATE DISTRIBUTOR =============
  const handleCreateDistributor = async (e) => {
    e.preventDefault();

    if (!newDistributor.partyId || !newDistributor.name) {
      alert("Distributor ID and Name are required.");
      return;
    }

    const id = newDistributor.partyId.trim();

    const payload = {
      partyId: id,
      name: newDistributor.name || "",
      address: newDistributor.address || "",
      route: newDistributor.route || "",
      distance: newDistributor.distance || "",
      gstno: newDistributor.gstno || "",
      phone: newDistributor.phone || "",
      clientId: newDistributor.clientId || "",
      vehicleno: newDistributor.vehicleno || "",
      dms_name: newDistributor.dms_name || "",
      easycig_name: newDistributor.easycig_name || "",
      partytype: newDistributor.partytype || "distributor",
      creditLimit: newDistributor.creditLimit || "",
      opening_balance: newDistributor.opening_balance || "",
    };

    try {
      await setDoc(doc(db, "partyMaster", id), payload);
      alert("Distributor created/updated successfully!");

      setNewDistributor({
        partyId: "",
        name: "",
        address: "",
        route: "",
        distance: "",
        gstno: "",
        phone: "",
        clientId: "",
        vehicleno: "",
        dms_name: "",
        easycig_name: "",
        partytype: "distributor",
        creditLimit: "",
        opening_balance: "",
      });

      loadParties();
      setActiveTab("edit");
    } catch (err) {
      console.error(err);
      alert("Error saving distributor");
    }
  };

  // ============= TAB: EDIT PARTIES =============

  const handleSearch = (text) => {
    const s = text.toLowerCase();
    const results = parties.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(s) ||
        (p.partyId || "").toLowerCase().includes(s)
    );
    setFiltered(results);
  };

  const saveDistributorChanges = async (party) => {
    try {
      await updateDoc(doc(db, "partyMaster", party.id), {
        phone: party.phone || "",
        vehicleno: party.vehicleno || "",
      });
      alert("Updated successfully!");
      loadParties();
    } catch (err) {
      console.error(err);
      alert("Error updating distributor");
    }
  };

  const deleteDistributor = async (party) => {
    const ok = window.confirm(
      `Are you sure you want to DELETE distributor "${party.name}" (${party.partyId})?`
    );
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "partyMaster", party.id));
      alert("Distributor deleted.");
      loadParties();
    } catch (err) {
      console.error(err);
      alert("Error deleting distributor");
    }
  };

  const handleExportData = () => {
    if (parties.length === 0) {
      alert("No data to export.");
      return;
    }
    const exportData = parties.map(p => ({
      "Distributor ID": p.partyId || "",
      "Name": p.name || "",
      "Address": p.address || "",
      "Route": p.route || "",
      "Distance": p.distance || "",
      "GST Number": p.gstno || "",
      "Phone": p.phone || "",
      "Client ID": p.clientId || "",
      "Vehicle Number": p.vehicleno || "",
      "DMS Name": p.dms_name || "",
      "EasyCig Name": p.easycig_name || "",
      "Party Type": p.partytype || "",
      "Credit Limit": p.creditLimit || 0,
      "Opening Balance": p.opening_balance || 0
    }));

    exportToExcel({
      data: exportData,
      fileName: `Distributor_Master_Export_${new Date().toISOString().split('T')[0]}`,
      sheetName: "Distributors"
    });
  };

  // ============= TAB: BULK UPLOAD =============

  const handleCsvFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        setCsvPreview(result.data);
      },
    });
  };

  const uploadCsvToFirestore = async () => {
    if (csvPreview.length === 0) {
      alert("No CSV data to upload.");
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const row of csvPreview) {
      try {
        const id = (row.partyId || row.PartyID || "").trim();
        if (!id) {
          failCount++;
          continue;
        }

        const payload = {
          partyId: id,
          name: row.name || row.PARTYNAME || "",
          address: row.address || "",
          route: row.route || "",
          distance: row.distance || "",
          gstno: row.gstno || "",
          phone: row.phone || "",
          clientId: row.clientId || "",
          vehicleno: row.vehicleno || "",
          dms_name: row.dms_name || "",
          easycig_name: row.easycig_name || "",
          partytype: row.partytype || "distributor",
          creditLimit: row.creditLimit || "",
          opening_balance: row.opening_balance || "",
        };

        await setDoc(doc(db, "partyMaster", id), payload, { merge: true });
        successCount++;
      } catch (err) {
        console.error("Error on row:", row, err);
        failCount++;
      }
    }
    alert(`Bulk upload complete.\nSuccess: ${successCount}\nFailed: ${failCount}`);
    loadParties();
    setActiveTab("edit");
  };

  return (
    <Layout>
      <div className="layout-content">
        <div style={{ marginBottom: 40 }}>
          <h1 style={{ fontSize: 32, marginBottom: 8 }}>Entity Command Center</h1>
          <p style={{ color: "var(--text-muted)" }}>Provision parent distributors, manage logistics credentials, and oversee the global partner network.</p>
        </div>

        <div style={{ 
          display: "flex", 
          gap: 8, 
          marginBottom: 32, 
          padding: 6, 
          background: "var(--surface-hover)", 
          borderRadius: 14, 
          width: "fit-content",
          border: "1px solid var(--border)"
        }}>
          {[
            { id: "create", label: "Provision Entity", hideFor: ["rsm"] },
            { id: "edit", label: "Partner Registry" },
            { id: "bulk", label: "Global Ingest", hideFor: ["rsm"] }
          ].filter(tab => !tab.hideFor || !tab.hideFor.includes(userProfile?.role)).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "10px 20px",
                borderRadius: 10,
                border: "none",
                background: activeTab === tab.id ? "var(--surface)" : "transparent",
                color: activeTab === tab.id ? "var(--primary)" : "var(--text-muted)",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: activeTab === tab.id ? "var(--shadow-sm)" : "none"
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "create" && userProfile?.role !== "rsm" && (
          <div className="premium-card" style={{ maxWidth: 1000, padding: 32 }}>
            <h2 style={{ fontSize: 22, marginBottom: 24 }}>System Provisioning: Distributor Entity</h2>
            <form onSubmit={handleCreateDistributor}>
               <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 24, marginBottom: 24 }}>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Entity Identifier (Party ID)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newDistributor.partyId}
                    onChange={(e) => onChangeNewDistributor("partyId", e.target.value)}
                    required
                    style={{ width: "100%" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Legal Entity Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newDistributor.name}
                    onChange={(e) => onChangeNewDistributor("name", e.target.value)}
                    required
                    style={{ width: "100%" }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Operational Headquarters (Address)</label>
                <input
                  type="text"
                  className="form-input"
                  value={newDistributor.address}
                  onChange={(e) => onChangeNewDistributor("address", e.target.value)}
                  style={{ width: "100%" }}
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24, marginBottom: 24 }}>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Fiscal Identity (GSTIN)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newDistributor.gstno}
                    onChange={(e) => onChangeNewDistributor("gstno", e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Verified Contact</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newDistributor.phone}
                    onChange={(e) => onChangeNewDistributor("phone", e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Logistics Cluster (Route)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newDistributor.route}
                    onChange={(e) => onChangeNewDistributor("route", e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
              </div>

              <div style={{ padding: 24, borderRadius: 16, background: "var(--surface-hover)", border: "1px solid var(--border)", marginBottom: 32 }}>
                <h4 style={{ margin: "0 0 20px 0", color: "var(--primary)", fontSize: 14, fontWeight: 800 }}>LOGISTICS & FINANCIAL ATTRIBUTES</h4>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 6 }}>CREDIT CAP</label>
                    <input type="number" className="form-input" value={newDistributor.creditLimit} onChange={e => onChangeNewDistributor("creditLimit", e.target.value)} style={{ width: "100%", background: "white" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 6 }}>OPENING BALANCE</label>
                    <input type="number" className="form-input" value={newDistributor.opening_balance} onChange={e => onChangeNewDistributor("opening_balance", e.target.value)} style={{ width: "100%", background: "white" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 6 }}>FLEET REG</label>
                    <input type="text" className="form-input" value={newDistributor.vehicleno} onChange={e => onChangeNewDistributor("vehicleno", e.target.value)} style={{ width: "100%", background: "white" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 6 }}>LEGACY SYS ID</label>
                    <input type="text" className="form-input" value={newDistributor.dms_name} onChange={e => onChangeNewDistributor("dms_name", e.target.value)} style={{ width: "100%", background: "white" }} />
                  </div>
                </div>
              </div>

              <button type="submit" className="btn-primary" style={{ width: "100%", padding: 18, fontSize: 18 }}>
                Initialize Partner Authorization
              </button>
            </form>
          </div>
        )}

        {activeTab === "edit" && (
          <div className="premium-card" style={{ padding: 0 }}>
            <div style={{ padding: "24px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ position: "relative", width: 400 }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Pulse search by identity or name..."
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{ width: "100%", paddingLeft: 40 }}
                />
                <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }}>🔍</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <button 
                  onClick={handleExportData}
                  className="btn-secondary"
                  style={{ padding: "10px 20px", fontSize: 13, borderRadius: 10 }}
                >
                  📥 Export Excel
                </button>
                <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 700 }}>{filtered.length} PARTNERS TRACKED</div>
              </div>
            </div>

            <div style={{ maxHeight: 700, overflowY: "auto" }}>
              <table className="enterprise-table">
                <thead>
                  <tr>
                    <th>Entity Identity</th>
                    <th>Logistics Reach</th>
                    <th>Fiscal Metadata</th>
                    <th>Portal Access</th>
                    {userProfile?.role !== "rsm" && <th style={{ textAlign: "right" }}>Management</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan="5" style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Network silent.</td></tr>
                  ) : (
                    filtered.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <div style={{ fontWeight: 800, color: "var(--primary)", fontSize: 15 }}>{p.name}</div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>ID: {p.partyId}</div>
                        </td>
                        <td>
                          <div style={{ fontSize: 12, fontWeight: 600 }}>{p.address || "N/A HQ"}</div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>Route: {p.route || "Global"}</div>
                        </td>
                        <td>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)" }}>GST: {p.gstno || "N/A"}</div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>Limit: ₹{p.creditLimit || 0}</div>
                        </td>
                        <td>
                          {p.hasLogin ? (
                            <div className="tb-status-pill status-online" style={{ display: "inline-flex" }}>VERIFIED PULSE</div>
                          ) : (
                            userProfile?.role !== "rsm" ? (
                              <button
                                onClick={() => { setSelectedPartyForLogin(p); setShowLoginModal(true); }}
                                style={{ border: "none", background: "rgba(99, 102, 241, 0.1)", color: "var(--primary)", padding: "4px 12px", borderRadius: 8, fontSize: 10, fontWeight: 800, cursor: "pointer" }}
                              >
                                PROVISION ACCESS
                              </button>
                            ) : (
                              <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 700 }}>ACCESS PENDING</div>
                            )
                          )}
                        </td>
                        {userProfile?.role !== "rsm" && (
                          <td style={{ textAlign: "right" }}>
                             <button onClick={() => deleteDistributor(p)} style={{ border: "none", background: "rgba(239, 68, 68, 0.1)", color: "var(--error)", padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 800, cursor: "pointer" }}>Retire</button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "bulk" && userProfile?.role !== "rsm" && (
          <div className="premium-card" style={{ maxWidth: 840, padding: 32 }}>
            <h2 style={{ fontSize: 22, marginBottom: 12 }}>Global Network Ingest</h2>
            <div style={{ padding: 40, borderRadius: 20, background: "var(--surface-hover)", border: "2px dashed var(--border)", textAlign: "center", marginBottom: 32 }}>
              <input type="file" id="dist_csv" style={{ display: "none" }} accept=".csv" onChange={handleCsvFile} />
              <label htmlFor="dist_csv" className="btn-primary" style={{ cursor: "pointer", padding: "14px 28px" }}>Execute Ingest Protocol</label>
            </div>
            {csvPreview.length > 0 && (
                <button onClick={uploadCsvToFirestore} className="btn-primary" style={{ width: "100%", padding: 16 }}>Confirm Global Infusion ({csvPreview.length} items)</button>
            )}
          </div>
        )}
      </div>

      {showLoginModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15, 23, 42, 0.8)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div className="premium-card" style={{ maxWidth: 460, width: "100%", padding: 32 }}>
            <h3 style={{ fontSize: 20, marginBottom: 8 }}>Secure Portal Provisioning</h3>
            <form onSubmit={handleCreateLogin}>
              <input type="email" className="form-input" required style={{ width: "100%", marginBottom: 16 }} value={loginForm.email} onChange={e => setLoginForm(prev => ({ ...prev, email: e.target.value }))} placeholder="admin@partner.com" />
              <input type="text" className="form-input" required style={{ width: "100%", marginBottom: 24 }} value={loginForm.password} onChange={e => setLoginForm(prev => ({ ...prev, password: e.target.value }))} placeholder="Password" />
              <div style={{ display: "flex", gap: 12 }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>Finalize Authorization</button>
                <button type="button" onClick={() => setShowLoginModal(false)} className="btn-secondary">Abort</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default DistributorManager;
